import { UUIButtonSize, UUIButtonStyle, UUIButtonTheme, UUIButtonVariant } from '../../../types/UUIButton'

const buildBaseClassName = (size: UUIButtonSize = 'normal'): string => {
  const base = [
    ' tw:rounded-[48px] tw:flex tw:border-none tw:font-ubuntu tw:outline-none tw:py-1 tw:cursor-pointer',
    /// disabled
    'tw:disabled:shadow-none tw:disabled:cursor-default tw:disabled:active:ring-transparent',
    //transitions
    'tw:transition-all tw:duration-300 tw:ease-in-out'
  ].join(' ')

  let sizeClassName = ''
  switch (size) {
    case 'small':
      sizeClassName = 'tw:px-2 tw:py-1 tw:text-[12px] tw:font-normal tw:leading-[16px] tw:gap-[4px]'
      break
    case 'normal':
    default:
      sizeClassName = 'tw:py-1.5 tw:px-3 tw:text-[14px] tw:font-normal tw:leading-[20px] tw:gap-[4px]'
      break
  }
  return `${base} ${sizeClassName}`
}

const buildVariantStyleClassName = (
  variant: UUIButtonVariant = 'normal',
  style: UUIButtonStyle = 'darkBlue'
): string => {
  let variantClassName = ''
  let styleClassName = ''

  if ('normal' === variant) {
    variantClassName = [
      'tw:border-0',
      // hover
      'tw:hover:ring-4',
      //active
      'tw:active:ring-4',
      // focus-within
      'tw:focus-within:ring-4',
      // hover & focus-within
      'tw:hover:focus:ring-4',
      // disabled
      'tw:disabled:bg-none tw:disabled:text-grey-500 tw:disabled:bg-grey-700 tw:cursor-default',
      // A disabled button must acknowledge nothing. Each style below paints hover / focus / press
      // colours without asking whether the button is disabled, so the disabled look is restored here
      // for every one of those states at once — a compound `disabled:<state>:` outranks the plain
      // `<state>:` it answers, whatever order the two land in.
      'tw:disabled:hover:bg-grey-700 tw:disabled:hover:text-grey-500 tw:disabled:hover:ring-transparent',
      'tw:disabled:focus-within:bg-grey-700 tw:disabled:focus-within:text-grey-500 tw:disabled:focus-within:ring-transparent',
      'tw:disabled:hover:focus:bg-grey-700 tw:disabled:hover:focus:text-grey-500 tw:disabled:hover:focus:ring-transparent',
      'tw:disabled:active:bg-grey-700 tw:disabled:active:text-grey-500 tw:disabled:active:ring-transparent'
    ].join(' ')

    switch (style) {
      case 'darkBlue':
      default: {
        styleClassName += [
          'tw:bg-blue-400 tw:text-grey-1000',
          // hover
          'tw:hover:bg-blue-300 tw:hover:ring-transparent',
          // focus-within
          'tw:focus-within:ring-blue-400/35',
          // hover & focus-within
          'tw:hover:focus:ring-blue-400/35  tw:hover:focus:bg-blue-300',
          // active
          'tw:active:ring-blue-400/35  tw:active:bg-blue-500'
        ].join(' ')
        break
      }
      case 'blue': {
        styleClassName += [
          'tw:bg-blue-100 tw:text-grey-1000',
          // hover
          'tw:hover:bg-blue-300 tw:hover:ring-transparent',
          // focus-within
          'tw:focus-within:ring-blue-100/35 tw:active:bg-blue-100',
          // hover & focus-within
          'tw:hover:focus:ring-blue-100/35  tw:hover:focus:bg-blue-300',
          // active
          'tw:active:ring-blue-100/35 tw:active:bg-blue-400'
        ].join(' ')
        break
      }
      case 'grey': {
        styleClassName += [
          'tw:bg-grey-700 tw:text-grey-200',
          // hover
          'tw:hover:bg-grey-600 tw:hover:ring-transparent',
          // focus-within
          'tw:focus-within:ring-grey-700/40 tw:focus-within:bg-grey-700',
          // hover & focus-within
          'tw:hover:focus:ring-grey-500/45  tw:hover:focus:bg-grey-600',
          // active
          'tw:active:ring-grey-500/45  tw:active:bg-grey-500'
        ].join(' ')
        break
      }
      case 'modernGrey': {
        styleClassName += [
          'tw:bg-grey-800 tw:text-primary-text tw:[&>span]:font-normal',
          // hover
          'tw:hover:bg-grey-700 tw:hover:ring-transparent',
          // focus-within
          'tw:focus-within:ring-grey-700/40 tw:focus-within:bg-grey-700',
          // hover & focus-within
          'tw:hover:focus:ring-grey-500/45  tw:hover:focus:bg-grey-600',
          // active
          'tw:active:ring-grey-500/45  tw:active:bg-grey-500'
        ].join(' ')
        break
      }
      case 'lightAqua': {
        styleClassName += [
          'tw:bg-aquamarine-10 tw:text-grey-200',
          // hover
          'tw:hover:bg-aquamarine-25 tw:hover:ring-transparent',
          // focus-within
          'tw:focus-within:ring-aquamarine-400/35 tw:focus-within:bg-aquamarine-10',
          // hover & focus-within
          'tw:hover:focus:ring-aquamarine-400/35  tw:hover:focus:bg-aquamarine-50',
          // active
          'tw:active:ring-aquamarine-400/35  tw:active:bg-aquamarine-500'
        ].join(' ')
        break
      }
      case 'red': {
        styleClassName += [
          'tw:bg-red-50 tw:text-grey-1000',
          // hover
          'tw:hover:bg-red-100 tw:hover:ring-transparent',
          // focus-within
          'tw:focus-within:ring-red-50/35',
          // hover & focus-within
          'tw:hover:focus:ring-red-50/35 tw:hover:focus:bg-red-100',
          // active
          'tw:active:ring-red-50/35 tw:active:bg-red-200'
        ].join(' ')
        break
      }
      case 'purple': {
        styleClassName += [
          'tw:bg-purple-10 tw:bg-[linear-gradient(97.16deg,#8C3375_0%,#0F004B_100%)] tw:text-grey-1000',
          // hover
          'tw:hover:bg-none tw:hover:bg-purple-10 tw:hover:ring-transparent',
          // focus-within
          ' tw:focus-within:ring-purple-10/35',
          // hover & focus-within
          'tw:hover:focus:bg-none tw:focus:hover:bg-purple-10 tw:hover:focus:ring-purple-10/35',
          // active
          'tw:active:bg-none tw:active:bg-purple-25 tw:active:ring-purple-10/35'
        ].join(' ')
        break
      }
    }
  } else if ('text' === variant) {
    variantClassName = [
      'tw:bg-transparent tw:border-0 tw:underline tw:decoration-dotted tw:underline-offset-3 tw:decoration-from-font',
      // hover
      'tw:hover:bg-grey-800',
      // focus-within
      'tw:focus-within:bg-grey-800',
      // disabled
      'tw:disabled:text-grey-600 tw:cursor-default',
      // Same guard as the solid family: the styles below paint hover / focus / press colours
      // regardless of disabled, and the text button's resting look is a transparent background
      // with grey type, so that is what every one of those states is held to.
      'tw:disabled:hover:bg-transparent tw:disabled:hover:text-grey-600',
      'tw:disabled:focus-within:bg-transparent tw:disabled:focus-within:text-grey-600',
      'tw:disabled:hover:focus:bg-transparent tw:disabled:hover:focus:text-grey-600',
      'tw:disabled:active:bg-transparent tw:disabled:active:text-grey-600'
    ].join(' ')

    switch (style) {
      case 'blue':
      default: {
        styleClassName += [
          'tw:text-blue-100',
          // hover
          'tw:hover:text-blue-200 tw:hover:bg-transparent',
          // focus-within
          'tw:focus-within:bg-aquamarine-25',
          // hover & focus-within
          'tw:hover:focus:bg-aquamarine-25 tw:hover:focus:text-blue-200',
          // active
          'tw:active:bg-aquamarine-25 tw:active:text-blue-200'
        ].join(' ')
        break
      }

      case 'grey': {
        styleClassName += [
          'tw:text-grey-300',
          // hover
          'tw:hover:text-grey-400 tw:hover:bg-transparent',
          // focus-within
          'tw:focus-within:bg-grey-800',
          // hover & focus-within
          'tw:hover:focus:bg-grey-800 tw:hover:focus:text-grey-400',
          // active
          'tw:active:bg-grey-800 tw:active:text-grey-400'
        ].join(' ')
        break
      }
      case 'red': {
        styleClassName += [
          'tw:text-red-50',
          // hover
          'tw:hover:text-red-100 tw:hover:bg-transparent',
          // focus-within
          'tw:focus-within:bg-red-25',
          // hover & focus-within
          'tw:hover:focus:bg-red-25 tw:hover:focus:text-red-100',
          // active
          'tw:active:bg-red-25 tw:active:text-red-100'
        ].join(' ')
        break
      }
    }
  }
  return `${variantClassName} ${styleClassName}`
}

const buttonTheme: UUIButtonTheme = {
  element: ({ size, variant, style }) => `${buildBaseClassName(size)} ${buildVariantStyleClassName(variant, style)}`,
  label: () => `tw:flex tw:items-center tw:justify-center tw:select-none tw:px-1 tw:w-full`,
  leftIcon: () => 'tw:mr-1 tw:flex tw:items-center tw:justify-center',
  rightIcon: () => 'tw:ml-1 tw:flex'
}

export default buttonTheme
